import React, { memo, useMemo, useState, useRef } from "react";

import DataGrid, {
  Column,
  GroupPanel,
  HeaderFilter,
  Export,
  Paging,
  Item,
  Toolbar,
  StateStoring,
  SearchPanel,
  MasterDetail,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";

import { useFetch } from "../../../hooks/useFetch";
import { Button } from "devextreme-react/button";
import { TypeRun } from "../types";

type RunEventsProps = {
  runID: string;
  typeRun: TypeRun;
  days: number;
};

// Vitari Events tab (2026-09-14, urgent): AVITI rows never carry `details` (the field is
// omitempty on the backend's RunEvent), so this renders nothing extra for them — this
// grid's existing AVITI columns/behavior are otherwise untouched. For Vitari rows,
// `details` is the FULL payload telemetry-ingest captured off the wire (RunMetrics'
// Aggregate/Lanes/CycleMetrics/LaneCycleMetrics/PolonyMap*, RunProgress's step/substep,
// etc.) — expanded here rather than as flat columns because the grid is narrow and the
// field set varies per event kind ("do not drop fields" without listing every possible
// column up front).
const EventDetailTemplate = (props: { data: { details?: Record<string, unknown> } }) => {
  const details = props?.data?.details;
  if (!details || Object.keys(details).length === 0) {
    return (
      <div style={{ padding: 10, color: "#888" }}>
        No additional details for this event.
      </div>
    );
  }
  return (
    <pre
      style={{
        padding: 10,
        margin: 0,
        maxHeight: 400,
        overflow: "auto",
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {JSON.stringify(details, null, 2)}
    </pre>
  );
};

export default memo(({ runID, typeRun, days }: RunEventsProps) => {
  const fetchPrefix = typeRun === "Spatial" ? "spatialRun" : "run";
  const [forceRefresh, setForceRefresh] = useState(false);
  const {
    data: events,
    isLoading,
    refetch,
  } = useFetch(
    `/${fetchPrefix}/${runID}/events` +
      (forceRefresh ? `?days=${days + 1}&forceRefresh` : ""),
    !!runID
  );
  const grid = useRef();

  const [refetchRequested, setRefetchRequested] = useState(false);

  const resetOptions = useMemo(() => {
    return {
      hint: "Reset to default table layout",
      text: "   Reset",
      stylingMode: "text",
      onClick: () => {
        (grid.current as any).instance.state({});
      },
    };
  }, []);

  const spacerOptions = useMemo(() => {
    return {
      hint: "",
      text: "",
      stylingMode: "icon",
      disabled: true,
    };
  }, []);

  return (
    <div>
      <LoadPanel
        position={{ of: "#thegrid" }}
        visible={isLoading || refetchRequested}
        showIndicator={true}
        showPane={true}
      />

      <Button
        style={{ marginTop: 5 }}
        text="Refresh from Snowflake"
        icon="refresh"
        onClick={() => {
          setForceRefresh(true);
          setRefetchRequested(true);
          refetch().finally(() => setRefetchRequested(false));
        }}
      />

      <div style={{ margin: 10, display: "flex", justifyContent: "center" }}>
        <DataGrid
          dataSource={events}
          ref={grid}
          id="thegrid"
          keyExpr="id"
          height="80vh"
          width="100%"
          allowColumnReordering={true}
          showBorders={true}
          selection={{ mode: "single" }}
          columnAutoWidth
        >
          <Export
            enabled
            fileName={"EventsTable"}
            allowExportSelectedData={true}
          />
          <GroupPanel visible />
          <HeaderFilter visible />
          <SearchPanel visible />
          <Paging enabled={true} pageSize={100} />
          <StateStoring
            enabled={false}
            type="localStorage"
            storageKey="eventsTable"
          />
          <Toolbar>
            <Item name="columnChooserButton" />
            <Item name="groupPanel" />
            <Item name="searchPanel" />
            <Item name="exportButton" />
            <Item location="before" widget="dxButton" options={spacerOptions} />
            <Item location="before" widget="dxButton" options={resetOptions} />
          </Toolbar>
          <Column dataField="name" dataType="string" />
          <Column
            dataField="timestamp"
            dataType={"date"}
            sortIndex={0}
            sortOrder="asc"
            format={" HH:mm:ss (MMM dd) "}
          />
          <Column dataField="cycle" />
          {typeRun === "Sequencing" && <Column dataField="read" />}
          {typeRun === "Spatial" && <Column dataField="batch" />}
          {/* Vitari-only (2026-09-14): blank/omitted for every AVITI row, matching
              RunEvent's omitempty durationSec/details on the backend. */}
          <Column
            dataField="durationSec"
            caption="Duration (s)"
            dataType="number"
            format="#0.0"
          />
          <MasterDetail enabled={true} render={EventDetailTemplate} />
        </DataGrid>
      </div>
    </div>
  );
});
